import { useId } from "react";

import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";
import type { IconSegmentsProps } from "./IconSegments.types";
import "./controls.web.css";

export type { IconSegment } from "./IconSegments.types";

/**
 * `IconSegments` on the web: the same 44pt track and inset highlight as the native control
 * (`IconSegments.tsx` explains the geometry), drawn as a radio group of real radio inputs, so the
 * group is one tab stop, the arrow keys move between segments, and a screen reader hears "radio,
 * 1 of 2, checked". Each segment is a `label` around a visually hidden input, as
 * `ChoiceGroup.web.tsx` does, and carries the segment's `testID` for the e2e specs.
 */
export function IconSegments<V extends string>(
  { segments, value, onChange, label, testID }: IconSegmentsProps<V>,
) {
  const c = useThemeColors();
  const name = useId();
  return (
    <div
      role="radiogroup"
      aria-label={label}
      data-testid={testID}
      className="flex h-11 flex-row items-center self-start rounded-lg border border-border bg-surface p-0.5"
    >
      {segments.map(({ value: v, label: segmentLabel, icon: Glyph, testID: segmentTestID }) => {
        const active = v === value;
        return (
          <label
            key={v}
            data-testid={segmentTestID}
            title={segmentLabel}
            className="icon-segment flex h-[38px] w-[38px] cursor-pointer items-center justify-center rounded-[11px]"
          >
            <input
              type="radio"
              name={name}
              value={v}
              checked={active}
              aria-label={segmentLabel}
              onChange={() => onChange(v)}
              className="desktop-sr-only"
            />
            <span
              className={cn(
                "flex h-[34px] w-[34px] items-center justify-center rounded-[9px]",
                active && "bg-accent-soft",
              )}
            >
              <Glyph size={18} color={active ? c.primary : c.foreground} aria-hidden />
            </span>
          </label>
        );
      })}
    </div>
  );
}
